// Network protocol between the browser client and the game server (JSON over
// WebSocket). The server runs the same deterministic movement code as the
// client: the client predicts its own player, the server is authoritative.
//
// One public server hosts many rooms: official rooms (permanent, one per
// featured map) and rooms created by players (public or private with an
// invite code). Every room runs its own simulation of its map.

import type { Vec3 } from '../math';
import type { MapRulesInfo } from '../mapInfo';
import type { PlayerState, UserCmd } from '../physics/pmove';
import type { MoverEvent, MoverSnapshot } from '../game/movers';
import type { SessionSound } from '../game/session';

/**
 * 3: rooms, lobby, guest identities, map bundles. 4: player model bundle, animation state of players.
 * 5: asset bundles as a list (player model, sounds). 6: spectators (follow another player).
 * 7: save slots, practice rooms with goto / call. 8: server records of maps, player profiles.
 * 9: shared movers (room clock, mover events). 10: admins, map list updates, map difficulty / tags / ratings.
 * 12: teams (Axis / Allies spawns, team only triggers and doors), rotating doors.
 * 13: no records, leaderboards, profiles or map ratings.
 */
export const PROTOCOL_VERSION = 13;

/** Tags admins can give maps (what kind of jumps they have). */
export const MAP_TAGS = ['strafe', 'overbounce', 'slick', 'ladder', 'prone', 'movers', 'speed', 'precision', 'long', 'short', 'beginner'] as const;
/** Default port of the game server (HTTP + WebSocket on the same port). */
export const DEFAULT_PORT = 27961;

export type PlayerAction = 'save' | 'load' | 'respawn' | 'noclip';

export interface MapInfo {
  name: string;
  /** Self-contained pk3 with the map and everything it uses, downloadable from /bundle/<file>. */
  bundle: string;
  bundleSize: number;
  /** CRC32 of the .bsp, to make sure client and server use the same map. */
  checksum: number;
  /** Players on this map, all rooms together. */
  players: number;
  /** Timerun names of the map (ETJump "name" keys, "default" when unnamed). */
  runs: string[];
  rules: MapRulesInfo;
  /** From the .arena file or the worldspawn message; may contain colour codes. */
  longname?: string;
  author?: string;
  briefing?: string;
  /** A preview image is served at /levelshot/<name>. */
  levelshot: boolean;
  /** 1 (easy) .. 5 (hard), set by the server's admins. */
  difficulty: number | null;
  /** MAP_TAGS the admins gave the map. */
  tags: string[];
}

/** What the admin panel shows (GET /admin/status). */
export interface AdminStatus {
  maps: { name: string; source: string | null; bundle: string; bundleSize: number; official: boolean; players: number; difficulty: number | null; tags: string[] }[];
  /** Every room, private ones too. */
  rooms: RoomInfo[];
  uploadDir: string;
  maxUploadBytes: number;
}

/** A room as listed in the lobby. */
export interface RoomInfo {
  id: string;
  name: string;
  map: string;
  players: number;
  maxPlayers: number;
  /** Private rooms are only listed to their members (joined with the invite code). */
  private: boolean;
  /** Permanent server room without an owner. */
  official: boolean;
  owner: string | null;
  /** Practice room: players may goto / call each other (not while timing a run). */
  practice: boolean;
}

export interface RoomMember {
  id: number;
  name: string;
  /** Watching another player instead of playing. */
  spectating: boolean;
  /** Axis (0) or Allies (1). */
  team: 0 | 1;
}

export interface MapVote {
  map: string;
  /** Who started it. */
  by: string;
  yes: number;
  no: number;
  /** Yes votes needed (a majority of the room when the vote started). */
  needed: number;
  /** ms left. */
  remaining: number;
}

/** What members see about their room. */
export interface RoomState extends RoomInfo {
  ownerId: number | null;
  /** Invite code (members only; the way into a private room). */
  code: string;
  members: RoomMember[];
  vote: MapVote | null;
}

export interface CreateRoomOptions {
  name: string;
  map: string;
  maxPlayers: number;
  private: boolean;
  /** goto / call allowed. */
  practice?: boolean;
}

// --- client -> server ---

export type ClientMessage =
  /** token: the guest identity from an earlier welcome (keeps the name). */
  | { t: 'hello'; version: number; name: string; token?: string }
  | { t: 'setName'; name: string }
  /** Lobby updates (room list) on or off. */
  | { t: 'lobby'; subscribe: boolean }
  /** msec: physics frame length, seeds the command clock. */
  | { t: 'createRoom'; room: CreateRoomOptions; msec: number }
  | { t: 'joinRoom'; id?: string; code?: string; msec: number }
  /** The fullest public room (of the map, if given) with space, or a new one. */
  | { t: 'quickPlay'; map?: string; msec: number }
  | { t: 'leaveRoom' }
  /** Owner: change the map right away. */
  | { t: 'setRoomMap'; map: string }
  /** Owner: remove a player from the room. */
  | { t: 'kick'; player: number }
  /** Start a vote to change the map. */
  | { t: 'voteMap'; map: string }
  | { t: 'vote'; yes: boolean }
  | { t: 'cmd'; cmd: UserCmd }
  /** Become a spectator following a player (id, or the next / previous one), or play again ('off'). */
  | { t: 'spectate'; target: number | 'next' | 'prev' | 'off' }
  /** slot: save / load position slot 0..2 (default 0). */
  | { t: 'action'; action: PlayerAction; slot?: number }
  /** Join Axis (0) or Allies (1): respawn at the team's spawn (kept across the room's map changes). */
  | { t: 'setTeam'; team: 0 | 1 }
  /** Practice rooms: teleport to a standing player / bring one to us (ETJump goto / call). */
  | { t: 'goto'; player: number }
  | { t: 'call'; player: number }
  | { t: 'chat'; text: string }
  /** Latency probe, answered with a pong carrying the same id. */
  | { t: 'ping'; id: number };

// --- server -> client ---

export interface RemotePlayer {
  id: number;
  name: string;
  origin: Vec3;
  viewangles: Vec3;
  velocity: Vec3;
  crouched: boolean;
  prone: boolean;
  noclip: boolean;
  /** Name of the run being timed, or null. */
  running: string | null;
  /** Locomotion movetype PM_Footsteps asked for (shared/game/movetype.ts MOVETYPE, 0 = keep) and its fallback. */
  moveType: number;
  moveFallback: number;
  /** Legs yaw relative to the view (ps->movementDir). */
  movementDir: number;
  /** Last animation event (ANIM_EVENT) and its counter: a new counter value plays the event once. */
  animEvent: number;
  animEventSeq: number;
}

/** What a spectator gets about the followed player, every snapshot. */
export interface FollowState {
  id: number;
  name: string;
  /** The followed player's full state after its last command (like `you`). */
  ps: PlayerState;
  health: number;
  /** Its movers (they follow each player's own clock). */
  movers: MoverSnapshot;
  /** Its last command's movement (keys on the HUD, strafe helper). */
  cmd: { forwardmove: number; rightmove: number; upmove: number; buttons: number; wbuttons: number } | null;
  /** Axis (0) or Allies (1): the sleeve of its knife in first person. */
  team?: 0 | 1;
  /** The timerun it is on and the time so far. */
  timer: { run: string; time: number } | null;
  /** Sounds it made since the previous snapshot. */
  sounds: SessionSound[];
}

/** A download every map uses: the character model (pk3 with the model, animations and textures) or the player sounds. */
export interface AssetBundleInfo {
  kind: 'player' | 'sound';
  /** File name, downloadable from /bundle/<file>; carries its checksum. */
  bundle: string;
  size: number;
}

export type ServerMessage =
  /** token is set when a new guest identity was created (the client stores it). */
  | { t: 'welcome'; id: number; pid: string; name: string; token?: string; serverName: string; maps: MapInfo[]; assets: AssetBundleInfo[]; admin: boolean }
  /** The map list changed (an admin added, rebuilt or removed a map). */
  | { t: 'maps'; maps: MapInfo[] }
  | { t: 'nameChanged'; name: string }
  | { t: 'rooms'; rooms: RoomInfo[] }
  /** Joined a room (or its map changed): load the map, the server already runs the player there. */
  /** serverTime: the room's clock (ms); commands run on it, so every player's movers are in the same phase. */
  | { t: 'roomJoined'; room: RoomState; map: MapInfo; rules: { noJumpDelay: boolean; noFallDamage: number; noOverbounce: boolean; noWallbug: boolean }; serverTime: number }
  | { t: 'roomUpdate'; room: RoomState }
  /** closed: an admin removed the room or its map. */
  | { t: 'roomLeft'; reason: 'left' | 'kicked' | 'closed' }
  | {
      t: 'snapshot';
      /** Server state of this client after its last processed command (moverTime: its movers' last frame); null while spectating. */
      you: { ps: PlayerState; health: number; lastCmdTime: number; moverTime: number | null; movers?: MoverSnapshot } | null;
      players: RemotePlayer[];
      /** What other players did to the movers, at the frames this player's movers apply it. */
      moverEvents?: MoverEvent[];
      /** Spectators: the followed player (null = nobody to follow). */
      follow?: FollowState | null;
    }
  | { t: 'chat'; from: string; text: string }
  | { t: 'info'; text: string }
  | { t: 'pong'; id: number }
  | { t: 'error'; text: string };

/** CRC-32 (IEEE) used for map checksums. */
export function crc32(data: Uint8Array): number {
  let table = crcTable;
  if (!table) {
    table = crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      table[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) crc = table[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}
let crcTable: Uint32Array | null = null;
